import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Timer as TimerIcon } from '@mui/icons-material';
import { COLORS, SECTION_STYLES } from './MAMCStyles';

const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [shouldShow, setShouldShow] = useState(true);

  useEffect(() => {
    // Finnish Standard Time is UTC+3 for summer time (when August 1 occurs)
    // So 9 AM Finnish time is 6 AM UTC
    const targetDate = new Date('2026-08-01T06:00:00Z');

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;

      // If the target time has passed, hide the timer
      if (difference <= 0) {
        setShouldShow(false);
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / 1000 / 60) % 60);
      const seconds = Math.floor((difference / 1000) % 60);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    // Calculate initially to determine if we should show the timer
    calculateTimeLeft();

    // Only set up the interval if we still need to show the timer
    let timer;
    if (shouldShow) {
      timer = setInterval(calculateTimeLeft, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [shouldShow]); // Added shouldShow to dependency array

  // If we shouldn't show the timer, return null
  if (!shouldShow) {
    return null;
  }

  const TimeUnit = ({ value, label }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: { xs: 1, sm: 2, md: 4 },
        position: 'relative',
        '&:not(:last-child)::after': {
          content: '":"',
          position: 'absolute',
          right: -4, // Adjust position for better alignment
          top: '40%', // Move up slightly to align with numbers
          transform: 'translateY(-50%)',
          color: COLORS.text.accent,
          fontSize: { xs: '1rem', sm: '1.3rem', md: '1.6rem' },
          fontWeight: 'bold',
          lineHeight: 0, // Adjust line height to improve vertical alignment
          paddingBottom: '0.1em', // Fine-tune vertical position
        },
      }}
    >
      <Typography
        variant="h3"
        sx={{
          color: COLORS.text.accent,
          fontSize: { xs: '1rem', sm: '1.6rem', md: '2.4rem' },
          fontWeight: 'bold',
          textShadow: '0 2px 8px rgba(218,165,32,0.5)',
          fontFamily: 'monospace',
          mb: { xs: 0.5, sm: 1 },
          lineHeight: 1, // Ensure consistent line height for better separator alignment
        }}
      >
        {String(value).padStart(2, '0')}
      </Typography>
      <Typography
        sx={{
          color: COLORS.text.secondary,
          fontSize: { xs: '0.4rem', sm: '0.55rem', md: '0.65rem' },
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        ...SECTION_STYLES.countdown,
        mb: { xs: 3, sm: 4, md: 6 },
        py: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box sx={{ textAlign: 'center', mb: { xs: 1, sm: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, sm: 2 },
            mb: { xs: 1, sm: 2 },
          }}
        >
          <TimerIcon
            sx={{ color: COLORS.secondary, fontSize: { xs: '0.8rem', sm: '1rem', md: '1.3rem' } }}
          />
          <Typography
            variant="h5"
            sx={{
              color: COLORS.text.primary,
              fontSize: { xs: '0.65rem', sm: '0.85rem', md: '1rem' },
              fontWeight: 'bold',
              letterSpacing: '1px',
            }}
          >
            MAMC 2026 Starts In
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1, md: 2 },
        }}
      >
        <TimeUnit value={timeLeft.days} label="Days" />
        <TimeUnit value={timeLeft.hours} label="Hours" />
        <TimeUnit value={timeLeft.minutes} label="Minutes" />
        <TimeUnit value={timeLeft.seconds} label="Seconds" />
      </Box>
    </Box>
  );
};

export default CountdownTimer;
